class Segment {
    constructor(type) {
        this.type = type
    }

    load() { throw 'Not implemented'; }
}


class RouteSegment extends Segment {
    constructor (route, state, data) {
        super('route');

        this.route = route;
        this.state = state;
        this.data  = data;
    }

    load() {
        return [{
            type : 'load-route',
            args: {
                route: this.route,
                state: this.state,
                data : this.data
            }
        }];
    }
}

class ComponentSegment extends Segment {
    constructor (component, data) {
        super('component');

        this.component = component;
        this.data  = data;
    }

    load() {
        return [{
            type : 'load-component',
            args: {
                component: this.component,
                data : this.data,
            }
        }];
    }
}

export class QuestionSegment extends ComponentSegment { }

class FunctionSegment extends Segment {
    constructor(functions) {
        super('function');

        this.functions = functions
    }

    load() {
        return this.functions;
    }
}

class ContainerSegment extends Segment {
    constructor() {
        super('container');

        this.active = 0;
        this.segments = [];
    }

    get activeSegment() {
        if (this.active >= this.segments.length) {
            return false;
        }

        let item = this.segments[this.active];
        return item && item.segment;
    }

    setActiveByIndex(index, ignoreAlreadyActive = true) {
        index = parseInt(index)

        if (!index) {
            return false;
        }

        index = this.segments.findIndex(s => s.index === index)

        if (index === -1) {
            return false;
        }

        if (!ignoreAlreadyActive && this.active === index) {
            return false;
        }

        this.active = index;
        return true
    }

    setActive(active, ignoreAlreadyActive = true) {
        if (active === undefined) {
            return false;
        }

        if (active >= this.segments.length) {
            return false;
        }

        if (!ignoreAlreadyActive && this.active === active) {
            return false;
        }
        
        this.active = active;
        return true;
    }

    addSegment(indexes, segment) {
        this.segments.push({ indexes, segment });
        this.segments.sort((a,b) => a.indexes[0] - b.indexes[0]);
    }

    load() {
        if (!this.activeSegment) {
            return false;
        }
        return this.activeSegment.load();
    }

    previous() {
        let segment = this.activeSegment;

        if (typeof segment.previous === 'function' && segment.previous()) {
            return true;
        }

        this.active--;

        return !!this.activeSegment;
    }

    next() {
        let segment = this.activeSegment;

        if (typeof segment.next === 'function' && segment.next()) {
            return true;
        }

        this.active++;

        return !!this.activeSegment;
    }
}

class QuestionContainerSegment extends ContainerSegment {
    load() {
        let segment = this.activeSegment;
        let actions = super.load();

        if (segment instanceof QuestionSegment) {
            actions.unshift({
                type: 'update-position',
                args: {
                    index: this.active
                }
            });
        }

        return actions;
    }

    getSegmentById(id) {
        return this.segments.map(s => s.segment).find((s) => s.data.question && s.data.question.id === id);
    }

    clearAnswers() {
        for (let segment of this.segments.map(s => s.segment)) {
            segment.data.answer = undefined;
        }
    }

    setAnswer(id, answer) {
        let segment = this.getSegmentById(id);
        if (segment) {
            segment.data.answer  = answer;
            segment.data.grading = answer && answer.grading;
        }
    }

    setActiveById(id, ignoreAlreadyActive = true) {
        let index = this.segments.findIndex(s => s.segment.data.question.id === id);

        if (index === -1) {
            return false;
        }

        if (!ignoreAlreadyActive && this.active === index) {
            return false;
        }

        this.active = index;
        return true;
    }

    get navigation() {
        return this.segments.map(s => s.segment).map((s, i) => ({
            id: s.data.question.id,
            active: i === this.active,
            isAnswered: !!s.data.answer,
        }));
    }
}

class SegmentRuleCollection {
    constructor(rules) {
        this.rules = rules;
    }

    match(values) {
        values = [values].flat();

        let occurrence, occurrences = [];
        for (let value of values) {
            if (occurrence = occurrences.find(o => o.value === value)) {
                occurrence.count += 1;
            } else {
                occurrences.push({ value, count: 1})
            }
        }

        return this.rules.find(r => r.match(occurrences));
    }
}

class SegmentRule {
    constructor(component) {
        this.component = component,
        this.rules = [];
    }

    addRule(match, condition, optional = false) {
        if (this.rules.find(r => r.match === match))
            throw 'SegmentRule already contains a rule for: ' + match;

        this.rules.push(new Rule(match, condition, optional));
        
        return this;
    }

    match(occurrences) {
        if (occurrences.some(o => this.rules.every(t => t.value !== o.value)))
            return false;

        for (let rule of this.rules) {
            let occurrence = occurrences.find(o => o.value === rule.value);

            if (!rule.match(occurrence && occurrence.count)) {
                return false;
            }
        }

        return true;
    }
}

class Rule {
    constructor(value, condition, optional = false) {
        this.value = value;
        this.condition = String(condition).replace(/\s/g, '');
        this.optional = optional;
    }

    match(count) {
        // No matching count only matches if the rule is optional
        if (count === undefined)
            return this.optional;

        // Shorthand for '> 0'
        if (this.condition === '*')
            return count > 0;

        let [, operator, value] = this.condition.match(/^(>|<|!)?(\d)$/);
        switch (operator) {
            case '>': return count >  value;
            case '<': return count <  value;
            case '!': return count != value;
            default:  return count == value;
        }
    }
}

export let QuestionSegmentRules = new SegmentRuleCollection([
    new SegmentRule('MovieplayerModule')
        .addRule('MovieplayerModule', '1'),
    
    new SegmentRule('TextModule')
        .addRule('TextModule', '1'),
    
    new SegmentRule('MultipleChoiceModule')
        .addRule('MultipleChoiceModule', '1'),
    
    new SegmentRule('SelfTestModule')
        .addRule('SliderModule', '> 1'),
    
    new SegmentRule('TowerOfHanoi')
        .addRule('TowerOfHanoiModule', '1'),
    
    new SegmentRule('CardSorting')
        .addRule('CardSortingModule', '1'),
    
    new SegmentRule('BalloonTest')
        .addRule('BalloonAnalogRiskTaskModule', '1'),
])

export let QuestionContainer = () => new QuestionContainerSegment();
export let Function = (...functions) => new FunctionSegment(functions);
